import { IDateTimeEvent, IEventRegistrationRefreshModel, ISpecialtyProgram, IUserProgramSelection, StepConstants, IAttendee, IEpiProps } from "../models/event-reg-refresh-models";

export const buildDateRangeString = (days: string[], selectedLanguage: string) => {
  if (!days || days.length === 0) return '';

  if (selectedLanguage.toLocaleLowerCase() == "fr-fr") {
    return buildDateRangeStringFrench(days);
  }

  // For a single day, just return the formatted date
  if (days.length === 1) {
    const firstDayMatch = days[0].match(/([A-Za-z]+)\s+(\d+),\s+(\d+)/);
    if (!firstDayMatch) return days[0];

    const [_, month, day, year] = firstDayMatch;
    return `${month} ${day}, ${year}`;
  }

  // For multiple days, get first and last day to create a range
  const firstDayMatch = days[0].match(/([A-Za-z]+)\s+(\d+),\s+(\d+)/);
  const lastDayMatch = days[days.length - 1].match(/([A-Za-z]+)\s+(\d+),\s+(\d+)/);

  if (!firstDayMatch || !lastDayMatch) return days[0];

  const [_, firstMonth, firstDay, firstYear] = firstDayMatch;
  const [__, lastMonth, lastDay, lastYear] = lastDayMatch;

  // If years are different, include both years
  if (firstYear !== lastYear) {
    return `${firstMonth} ${firstDay}, ${firstYear}-${lastMonth} ${lastDay}, ${lastYear}`;
  }

  // If months are different, include both months
  if (firstMonth !== lastMonth) {
    return `${firstMonth} ${firstDay}-${lastMonth} ${lastDay}, ${firstYear}`;
  }

  // If same month, just show month once with day range
  return `${firstMonth} ${firstDay}-${lastDay}, ${firstYear}`;
};

const buildDateRangeStringFrench = (days: string[]) => {
  //Examples:
  //days = ["nov. 03, 2025 (8:30 AM - 4:30 PM)"] => "nov. 03, 2025"
  //days = ["nov. 03, 2025 (8:30 AM - 4:30 PM)", "nov. 04, 2025 (8:30 AM - 4:30 PM)", "nov. 06, 2025 (8:30 AM - 4:30 PM)"] => "nov. 03-nov. 06, 2025"

  if (!days || days.length === 0) return '';

  // For a single day, just return the formatted date
  if (days.length === 1) {
    const firstDayMatch = days[0].match(/([\p{L}]+\.?)\s+(\d+),\s+(\d+)/u);
    if (!firstDayMatch) return days[0];

    const [_, month, day, year] = firstDayMatch;
    return `${month} ${day}, ${year}`;
  }

  // For multiple days, get first and last day to create a range
  const firstDayMatch = days[0].match(/([\p{L}]+\.?)\s+(\d+),\s+(\d+)/u);
  const lastDayMatch = days[days.length - 1].match(/([\p{L}]+\.?)\s+(\d+),\s+(\d+)/u);

  if (!firstDayMatch || !lastDayMatch) return days[0];

  const [_, firstMonth, firstDay, firstYear] = firstDayMatch;
  const [__, lastMonth, lastDay, lastYear] = lastDayMatch;

  // If years are different, include both years
  if (firstYear !== lastYear) {
    return `${firstMonth} ${firstDay}, ${firstYear}-${lastMonth} ${lastDay}, ${lastYear}`;
  }

  // If months are different, include both months
  if (firstMonth !== lastMonth) {
    return `${firstMonth} ${firstDay}-${lastMonth} ${lastDay}, ${firstYear}`;
  }

  // If same month, just show month once with day range
  return `${firstMonth} ${firstDay}-${lastDay}, ${firstYear}`;
}
export const getDateTimeEventLocation = (dateTimeEvent: IDateTimeEvent, virtualLocationTextDateTimeCard: string) => {
  if (!dateTimeEvent) return '';

  if (dateTimeEvent.isVirtual) return virtualLocationTextDateTimeCard;

  const city = dateTimeEvent.city || '';
  const state = dateTimeEvent.state || '';

  if (!city && !state) return '';
  if (!city) return state;
  if (!state) return city;

  return `${city}, ${state}`;
}

/**
* Matches the user's selected program with a specialty program from the event registration model
* @param userProgramSelection The user's program selection
* @param eventRegistrationModel The complete event registration model containing all program data
* @returns The matched specialty program or undefined if no match is found
*/
export const matchUserProgramToSpecialtyProgram = (
  userProgramSelection: IUserProgramSelection | undefined,
  eventRegistrationModel: IEventRegistrationRefreshModel
): ISpecialtyProgram | undefined => {
  // Handle case where user hasn't made a selection yet
  if (!userProgramSelection || !userProgramSelection.programCode) {
    return undefined;
  }

  // Find the specialty program that matches the user's selection by program code and renewal status
  return eventRegistrationModel.specialtyPrograms.find(program =>
    program.programCode === userProgramSelection.programCode &&
    program.isRenewal === userProgramSelection.isRenewal &&
    program.isDirectDelivery === userProgramSelection.isDirectDelivery
  );
};

export const calculateVisitedSteps = (currentStep: string): string[] => {
  const stepOrder = [
    StepConstants.ProgramStep,
    StepConstants.AddOnsStep,
    StepConstants.DateTimeSpecialtyProgramStep,
    StepConstants.DateTimeAddOnProgramStep,
    StepConstants.AttendeesStep,
    StepConstants.ReviewStep
  ];
  
  const currentIndex = stepOrder.indexOf(currentStep);
  if (currentIndex === -1) {
    // If current step is not found, default to just the program step
    return [StepConstants.ProgramStep];
  }
  
  return stepOrder.slice(0, currentIndex + 1);
};

// Attendees Step: Validation helper functions
export const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
};

export const findDuplicateEmails = (attendees: IAttendee[]): string[] => {
  const emailCounts = new Map<string, number>();
  
  attendees.forEach(attendee => {
    const email = attendee.email.trim().toLowerCase();
    if (email) {
      emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
    }
  });
  
  return Array.from(emailCounts.entries())
    .filter(([_, count]) => count > 1)
    .map(([email, _]) => email);
};

export const hasRequiredFields = (attendee: IAttendee): boolean => {
  return !!(
    attendee.firstName.trim() && 
    attendee.lastName.trim() && 
    attendee.email.trim() && 
    attendee.title.trim()
  );
};

export const createFieldErrors = (attendee: { id: string } & IAttendee, epiProps: IEpiProps): Partial<IAttendee> => {
  const errors: Partial<IAttendee> = {};
  
  if (!attendee.firstName.trim()) {
    errors.firstName = epiProps.fieldRequiredMessage;
  }
  if (!attendee.lastName.trim()) {
    errors.lastName = epiProps.fieldRequiredMessage;
  }
  if (!attendee.email.trim()) {
    errors.email = epiProps.fieldRequiredMessage;
  }
  if (!attendee.title.trim()) {
    errors.title = epiProps.fieldRequiredMessage;
  }
  
  return errors;
};

export const createEmailFormatErrors = (attendee: { id: string } & IAttendee, epiProps: IEpiProps): Partial<IAttendee> => {
  const errors: Partial<IAttendee> = {};
  
  if (attendee.email.trim() && !isValidEmail(attendee.email)) {
    errors.email = epiProps.fieldInvalidMessage;
  }
  
  return errors;
};

export const createDuplicateEmailErrors = (attendee: { id: string } & IAttendee, duplicateEmails: string[], epiProps: IEpiProps): Partial<IAttendee> => {
  const errors: Partial<IAttendee> = {};
  
  if (duplicateEmails.includes(attendee.email.trim().toLowerCase())) {
    errors.email = epiProps.duplicateEmailFieldErrorMessage || 'This email address is already being used by another attendee';
  }
  
  return errors;
};

export const createRenewalEligibilityErrors = (attendee: { id: string } & IAttendee, epiProps: IEpiProps): Partial<IAttendee> => {
  const errors: Partial<IAttendee> = {};

  if (attendee.eligibilityChecked && attendee.isEligible === false) {
    errors.email = epiProps.attendeeNotEligibleFormat
      ? epiProps.attendeeNotEligibleFormat.replace('{0}', attendee.email)
      : `${attendee.email} is not eligible for renewal`;
  }

  return errors;
};

export const getProgramsWithCompareDetails = (programs: ISpecialtyProgram[]): ISpecialtyProgram[] => {
  return programs.filter(program =>
    (program.mainFeatures && program.mainFeatures.length > 0));
};

export const resolveUrl = (url: string): string => {
  if (!url) return '';

  // If it's already an absolute URL, return as-is
  if (url.startsWith('http')) {
    return url;
  }

  // If it's an Episerver virtual path, remove the ~ and prepend origin
  if (url.startsWith('~/')) {
    return `${window.location.origin}${url.substring(1)}`;
  }

  // If it's a relative path, prepend origin
  return `${window.location.origin}${url}`;
};

const US_STATE_ABBREVIATIONS: Record<string, string> = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
  'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
  'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
  'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
  'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
  'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
  'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
  'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
  'wisconsin': 'WI', 'wyoming': 'WY', 'washington dc': 'DC',
  'washington d.c.': 'DC', 'district of columbia': 'DC',
}

export function normalizeStateInput(location: string): string {
  const key = location.trim().toLowerCase()
  return US_STATE_ABBREVIATIONS[key] ?? location
}

// Select Location & Date page: City/State + Zip/Postal Code split location filter

const CA_POSTAL_CODE_PATTERN = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/
const US_ZIP_PATTERN = /^\d{5}(?:-\d{4})?$/

const looksLikePostalCode = (value: string): boolean => {
  const trimmed = value.trim()
  return US_ZIP_PATTERN.test(trimmed) || CA_POSTAL_CODE_PATTERN.test(trimmed)
}

/**
 * Closed-filter heading: city/state first, zip/postal included when both are set so zip does not replace the city.
 */
export const formatLocationDisplay = (cityState: string, postalCode: string): string => {
  const trimmedCityState = cityState.trim()
  const trimmedPostalCode = postalCode.trim()

  if (trimmedCityState && trimmedPostalCode) {
    return `${trimmedCityState} · ${trimmedPostalCode}`
  }

  return trimmedCityState || trimmedPostalCode
}

/**
 * Splits a single location string into City/State and Zip/Postal Code fields.
 * Zip/postal values go to postalCode; everything else is treated as cityState.
 */
export const splitLocationInput = (locationInput: string | undefined): { cityState: string; postalCode: string } => {
  const trimmed = (locationInput ?? '').trim()
  if (!trimmed) {
    return { cityState: '', postalCode: '' }
  }

  return looksLikePostalCode(trimmed)
    ? { cityState: '', postalCode: trimmed }
    : { cityState: trimmed, postalCode: '' }
}
